import { useState } from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack
} from '@mui/material';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import ScreenSearchDesktopIcon from '@mui/icons-material/ScreenSearchDesktop';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import logo from '../assets/logo.png';

const features = [
  {
    icon: <PlayCircleOutlineIcon sx={{ fontSize: 36, color: '#234734' }} />,
    title: 'Interactive Demo',
    description: 'Experience our POS system firsthand with a guided tour',
  },
  {
    icon: <ScreenSearchDesktopIcon sx={{ fontSize: 36, color: '#234734' }} />,
    title: 'Feature Showcase',
    description: 'See how our features can transform your restaurant operations',
  },
  {
    icon: <CompareArrowsIcon sx={{ fontSize: 36, color: '#234734' }} />,
    title: 'Competitive Analysis',
    description: 'Compare Restro360 with other POS systems in the market',
  },
];

const contactInfo = [
  {
    icon: <EmailIcon sx={{ color: '#F9B233' }} />,
    label: 'Email',
    value: 'demo@restro360.com',
  },
  {
    icon: <PhoneIcon sx={{ color: '#F9B233' }} />,
    label: 'Phone',
    value: '+1 (555) 123-4567',
  },
];

function Demo() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    restaurantName: '',
    restaurantType: '',
    message: '',
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Handle form submission
    console.log(formData);
  };

  return (
    <Box sx={{ bgcolor: '#fff8ec', minHeight: '100vh', py: { xs: 4, md: 8 } }}>
      <Container maxWidth="lg">
        <Grid container spacing={6} alignItems="stretch">
          {/* Left: Hero & Features */}
          <Grid item xs={12} md={7}>
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  mb: 4,
                }}
              >
                <img
                  src={logo}
                  alt="Demo Preview"
                  style={{
                    maxHeight: 180,
                    maxWidth: '100%',
                    borderRadius: 16,
                    boxShadow: '0 4px 24px rgba(35,71,52,0.10)',
                    background: '#fff',
                    padding: 12,
                  }}
                />
              </Box>
              <Box>
                <Typography variant="h4" sx={{ color: '#234734', fontWeight: 700, mb: 2 }}>
                  What to Expect
                </Typography>
                <Grid container spacing={2}>
                  {features.map((feature, index) => (
                    <Grid item xs={12} sm={6} key={index}>
                      <Card
                        sx={{
                          bgcolor: '#fff',
                          border: '1.5px solid #F9B233',
                          boxShadow: '0 2px 8px rgba(249,178,51,0.06)',
                          p: 2,
                          height: '100%',
                        }}
                      >
                        <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                          <Box sx={{ mb: 1 }}>{feature.icon}</Box>
                          <Typography variant="h6" sx={{ color: '#234734', fontWeight: 700 }}>
                            {feature.title}
                          </Typography>
                          <Typography sx={{ color: '#234734', opacity: 0.8, textAlign: 'center' }}>
                            {feature.description}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Box>
          </Grid>

          {/* Right: Sticky Form */}
          <Grid item xs={12}>
            <Box id="demo-form" sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', minHeight: 400 }}>
              <Card
                sx={{
                  bgcolor: '#fff8ec',
                  border: '1.5px solid #F9B233',
                  boxShadow: '0 2px 12px rgba(249,178,51,0.06)',
                  maxWidth: 480,
                  width: '100%',
                  mx: 'auto',
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h4" gutterBottom sx={{ color: '#234734', fontWeight: 700 }}>
                    Request a Demo
                  </Typography>
                  <form onSubmit={handleSubmit}>
                    <Stack spacing={2}>
                      <TextField
                        required
                        fullWidth
                        label="Name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                      />
                      <TextField
                        required
                        fullWidth
                        label="Email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                      />
                      <TextField
                        required
                        fullWidth
                        label="Phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                      />
                      <TextField
                        required
                        fullWidth
                        label="Restaurant Name"
                        name="restaurantName"
                        value={formData.restaurantName}
                        onChange={handleChange}
                      />
                      <FormControl fullWidth required>
                        <InputLabel>Restaurant Type</InputLabel>
                        <Select
                          name="restaurantType"
                          value={formData.restaurantType}
                          onChange={handleChange}
                          label="Restaurant Type"
                        >
                          <MenuItem value="qsr">Quick Service Restaurant</MenuItem>
                          <MenuItem value="fine-dining">Fine Dining</MenuItem>
                          <MenuItem value="cafe">Cafe & Bakery</MenuItem>
                          <MenuItem value="food-truck">Food Truck</MenuItem>
                          <MenuItem value="bar">Bar & Brewery</MenuItem>
                        </Select>
                      </FormControl>
                      <TextField
                        fullWidth
                        label="Message"
                        name="message"
                        multiline
                        rows={4}
                        value={formData.message}
                        onChange={handleChange}
                      />
                      <Button
                        type="submit"
                        variant="contained"
                        sx={{
                          bgcolor: '#F9B233',
                          color: '#234734',
                          fontWeight: 700,
                          px: 4,
                          py: 1.5,
                          borderRadius: 2,
                          boxShadow: '0 2px 8px rgba(249,178,51,0.10)',
                          '&:hover': { bgcolor: '#ffe1a6', color: '#234734' },
                        }}
                        size="large"
                        fullWidth
                      >
                        Request Demo
                      </Button>
                    </Stack>
                  </form>
                </CardContent>
              </Card>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

export default Demo; 